import {Router} from '@multipass-system';
import {EventBus, Logger} from 'esp-js';

import {AccountService, LegalDocumentsService, reasonCodeOf, SessionService, SignupService} from '../../dal';
import {LocaleService} from '../locale';
import {
    AwaitingTermsRequiredEvent,
    DeleteAccountFailedEvent,
    LegalDocumentsLoadedEvent,
    LocaleChangedEvent,
    LocaleRestoredEvent,
    LoginErrorEvent,
    LoginLinkRequestedEvent,
    LoginSuccessEvent,
    ScriptPreferenceChosenEvent,
    ScriptPreferenceRejectedEvent,
    ScriptPreferenceUpdatedEvent,
    SessionEvents,
    SessionRestoredEvent,
    SignInExpiredEvent,
    SignInRejectedEvent,
    SignUpRejectedEvent,
    SignUpRequestedEvent,
    VerifyCodeRequestedEvent,
} from './events';
import {SessionStoreModel} from './sessionStoreModel';

const _log = Logger.create('SessionStore');

export const SESSION_STORE_ID = 'session-store';

export const registerSessionStore = (
    eventBus: EventBus,
    sessionService: SessionService,
    signupService: SignupService,
    legalDocumentsService: LegalDocumentsService,
    router: Router,
    accountService: AccountService,
    localeService: LocaleService
) => {
    eventBus.storeBuilder<SessionStoreModel>(SESSION_STORE_ID, new SessionStoreModel())
        .withEventHandler<LoginLinkRequestedEvent>(SessionEvents.loginLinkRequested, (draft, event) => {
            draft.onLoginLinkRequested(event.email);
        })
        .withEffect<LoginLinkRequestedEvent>(SessionEvents.loginLinkRequested, (model, event, eventContext, publish) => {
            sessionService.sendLoginCode(event.email, localeService.currentLocale)
                .then(outcome => {
                    if (outcome.kind === 'accountNotFound') {
                        publish(SessionEvents.signInRejected, {message: outcome.message, code: outcome.code} satisfies SignInRejectedEvent);
                    }
                })
                .catch(error => {
                    _log.error('Send login code failed', error);
                    publish(SessionEvents.loginError, {error: String(error), code: reasonCodeOf(error)} satisfies LoginErrorEvent);
                });
        })
        .withEventHandler<SignUpRequestedEvent>(SessionEvents.signUpRequested, (draft, event) => {
            draft.onSignUpRequested(event.email);
        })
        .withEffect<SignUpRequestedEvent>(SessionEvents.signUpRequested, (model, event, eventContext, publish) => {
            signupService.signUp({
                email: event.email,
                displayName: event.displayName,
                acceptedDocumentIds: event.acceptedDocumentIds,
                locale: localeService.currentLocale,
            })
                .then(outcome => {
                    if (outcome.kind === 'accountExists') {
                        publish(SessionEvents.signUpRejected, {message: outcome.message, code: outcome.code} satisfies SignUpRejectedEvent);
                    }
                })
                .catch(error => {
                    _log.error('Sign up failed', error);
                    publish(SessionEvents.loginError, {error: String(error), code: reasonCodeOf(error)} satisfies LoginErrorEvent);
                });
        })
        .withEffect(SessionEvents.legalDocumentsRequested, (model, event, eventContext, publish) => {
            legalDocumentsService.getCurrentDocuments()
                .then(documents => publish(SessionEvents.legalDocumentsLoaded, {documents} satisfies LegalDocumentsLoadedEvent))
                .catch(error => _log.error('Load legal documents failed', error));
        })
        .withEventHandler<LegalDocumentsLoadedEvent>(SessionEvents.legalDocumentsLoaded, (draft, event) => {
            draft.onLegalDocumentsLoaded(event.documents);
        })
        .withEventHandler<VerifyCodeRequestedEvent>(SessionEvents.verifyCodeRequested, draft => {
            draft.onVerifyCodeRequested();
        })
        .withEffect<VerifyCodeRequestedEvent>(SessionEvents.verifyCodeRequested, (model, event, eventContext, publish) => {
            const email = model.pendingEmail;
            if (email === null) {
                _log.warn('Ignoring code verification with no awaiting email on the state');
                return;
            }
            sessionService.verifyLoginCode(email, event.code)
                .then(outcome => {
                    if (outcome.kind === 'session') {
                        publish(SessionEvents.loginSuccess, {session: outcome.session} satisfies LoginSuccessEvent);
                    } else {
                        publish(SessionEvents.awaitingTermsRequired, {
                            outstandingLegalDocumentIds: outcome.outstandingLegalDocumentIds,
                            signInTicket: outcome.signInTicket,
                        } satisfies AwaitingTermsRequiredEvent);
                    }
                })
                .catch(error => {
                    _log.error('Login failed', error);
                    publish(SessionEvents.loginError, {error: String(error), code: reasonCodeOf(error)} satisfies LoginErrorEvent);
                });
        })
        .withEventHandler<LoginSuccessEvent>(SessionEvents.loginSuccess, (draft, event) => {
            draft.onLoginSuccess(event.session);
        })
        .withEffect(SessionEvents.loginSuccess, () => {
            router.navigate('/home');
        })
        .withEventHandler<AwaitingTermsRequiredEvent>(SessionEvents.awaitingTermsRequired, (draft, event) => {
            draft.onAwaitingTermsRequired(event.outstandingLegalDocumentIds, event.signInTicket);
        })
        .withEffect(SessionEvents.awaitingTermsRequired, (model, event, eventContext, publish) => {
            publish(SessionEvents.legalDocumentsRequested, {});
        })
        .withEffect(SessionEvents.acceptTermsRequested, (model, event, eventContext, publish) => {
            const signInTicket = model.signInTicket;
            if (signInTicket === null) {
                _log.warn('Ignoring accept-terms with no sign-in ticket on the state');
                return;
            }
            sessionService.acceptTermsAndSignIn(signInTicket, model.outstandingLegalDocumentIds)
                .then(session => publish(SessionEvents.loginSuccess, {session} satisfies LoginSuccessEvent))
                .catch(error => {
                    _log.error('Accept terms failed', error);
                    publish(SessionEvents.signInExpired, {message: String(error), code: reasonCodeOf(error)} satisfies SignInExpiredEvent);
                });
        })
        .withEventHandler<SignInExpiredEvent>(SessionEvents.signInExpired, (draft, event) => {
            draft.onSignInExpired(event.message, event.code);
        })
        .withEventHandler<SessionRestoredEvent>(SessionEvents.sessionRestored, (draft, event) => {
            draft.onSessionRestored(event.session);
        })
        .withEventHandler<LoginErrorEvent>(SessionEvents.loginError, (draft, event) => {
            draft.onLoginError(event.error, event.code);
        })
        .withEventHandler<SignInRejectedEvent>(SessionEvents.signInRejected, (draft, event) => {
            draft.onSignInRejected(event.message, event.code);
        })
        .withEventHandler<SignUpRejectedEvent>(SessionEvents.signUpRejected, (draft, event) => {
            draft.onSignUpRejected(event.message, event.code);
        })
        .withEventHandler(SessionEvents.loginRestartRequested, draft => {
            draft.onLoginRestarted();
        })
        .withEventHandler(SessionEvents.signOut, draft => {
            draft.onSignedOut();
        })
        .withEffect(SessionEvents.signOut, () => {
            sessionService.signOut().catch(error => _log.error('Sign out failed', error));
            router.navigate('/');
        })
        .withEventHandler(SessionEvents.deleteAccountRequested, draft => {
            draft.onDeleteAccountRequested();
        })
        .withEffect(SessionEvents.deleteAccountRequested, (model, event, eventContext, publish) => {
            const userId = model.userId;
            if (userId === null) {
                _log.warn('Ignoring delete-account with no signed-in user on the state');
                return;
            }
            accountService.deleteAccount(userId)
                .then(() => publish(SessionEvents.signOut, {}))
                .catch(error => {
                    _log.error('Delete account failed', error);
                    publish(SessionEvents.deleteAccountFailed, {error: String(error), code: reasonCodeOf(error)} satisfies DeleteAccountFailedEvent);
                });
        })
        .withEventHandler<DeleteAccountFailedEvent>(SessionEvents.deleteAccountFailed, (draft, event) => {
            draft.onDeleteAccountFailed(event.error, event.code);
        })
        .withEventHandler<LocaleChangedEvent>(SessionEvents.localeChanged, (draft, event) => {
            draft.onLocaleChanged(event.locale);
        })
        .withEffect<LocaleChangedEvent>(SessionEvents.localeChanged, (model, event) => {
            const userId = model.userId;
            localeService.setLocale(event.locale)
                .then(() => userId !== null ? accountService.updateLocale(userId, event.locale) : Promise.resolve())
                .catch(error => _log.error('Locale change failed', error));
        })
        .withEventHandler<LocaleRestoredEvent>(SessionEvents.localeRestored, (draft, event) => {
            draft.onLocaleChanged(event.locale);
        })
        .withEventHandler(SessionEvents.scriptPreferenceChosen, draft => {
            draft.onScriptPreferenceChosen();
        })
        .withEffect<ScriptPreferenceChosenEvent>(SessionEvents.scriptPreferenceChosen, (model, event, eventContext, publish) => {
            const userId = model.userId;
            if (userId === null) {
                _log.warn('Ignoring script-preference choice with no signed-in user on the state');
                return;
            }
            accountService.updateScriptPreference(userId, event.scriptPreference)
                .then(updated => publish(SessionEvents.scriptPreferenceUpdated, {
                    scriptPreference: updated.scriptPreference,
                    pendingDecision: updated.pendingDecision,
                } satisfies ScriptPreferenceUpdatedEvent))
                .catch(error => {
                    _log.error('Script preference update failed', error);
                    publish(SessionEvents.scriptPreferenceRejected, {message: String(error), code: reasonCodeOf(error)} satisfies ScriptPreferenceRejectedEvent);
                });
        })
        .withEventHandler<ScriptPreferenceUpdatedEvent>(SessionEvents.scriptPreferenceUpdated, (draft, event) => {
            draft.onScriptPreferenceUpdated(event.scriptPreference, event.pendingDecision);
        })
        .withEventHandler<ScriptPreferenceRejectedEvent>(SessionEvents.scriptPreferenceRejected, (draft, event) => {
            draft.onScriptPreferenceRejected(event.message, event.code);
        })
        .build();
};
